/**
 * SoleadLab 2.0 — Fault Detection API Client ("Find Similar Faults" P2a)
 *
 * Thin fetch wrappers for the five detection-job endpoints in
 * soleadlab-api/src/handlers/detections.ts. Job pattern mirrors tileApi:
 * POST creates the job (server dispatches fire-and-forget to the detect
 * container), the client polls GET until a terminal status, then fetches
 * the result JSON once.
 *
 * NOTE: these routes are admin/operator only and NEVER share-readable —
 * useDetectionPoller hard-skips share sessions so a share viewer's tab
 * never fires them.
 */

import { apiFetch, ApiError } from './apiFetch';

export { ApiError };

// ─── Types ───────────────────────────────────────────────────────────

export type DetectionScanMode = 'similar' | 'anomaly';

export type DetectionJobStatus =
  | 'pending'
  | 'running'
  | 'complete'
  | 'error'
  | 'cancelled';

/** POST /detections body (server validates; similar mode requires
 *  indicationType + 3–5 exemplarIndicationIds, anomaly forbids both). */
export interface CreateDetectionRequest {
  scanMode: DetectionScanMode;
  tilesetId: string;
  orthoLayerId: string;
  indicationType?: string;
  exemplarIndicationIds?: string[];
  params?: Record<string, unknown>;
}

/** 201 payload of POST /detections. */
export interface CreateDetectionResult {
  jobId: string;
  /** 'running' when the server dispatched to the detect container,
   *  'pending' when COG_INGEST_URL is unset (nothing dispatched). */
  status: 'pending' | 'running';
}

/** GET /detections/:jobId payload (detection_jobs row, camelCased). */
export interface DetectionJobInfo {
  id: string;
  siteId: string;
  tilesetId: string | null;
  orthoLayerId: string | null;
  scanMode: DetectionScanMode;
  indicationType: string | null;
  exemplarIds: string[] | null;
  params: Record<string, unknown> | null;
  status: DetectionJobStatus;
  error: string | null;
  candidateCount: number | null;
  resultR2Key: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Per-candidate scoring metadata written by detect_algo (verbatim). */
export interface DetectionCandidateProps {
  score01: number;
  s_raw?: number;
  mode: string;
  rotationDeg?: number;
  templateId?: number | null;
  exemplarId?: string | null;
  workingGsd?: number;
}

/** One raw result candidate: closed 5-point lng/lat ring + props. */
export interface DetectionResultCandidate {
  corners: [number, number][];
  props: DetectionCandidateProps;
}

/** GET /detections/:jobId/result payload — RAW JSON (no success envelope;
 *  the handler streams the R2 object body directly). */
export interface DetectionResult {
  candidates: DetectionResultCandidate[];
  summary: Record<string, unknown> | null;
}

/** POST /detections/:jobId/labels payload. */
export interface PostDetectionLabelsResult {
  saved: boolean;
  acceptedCount: number;
  rejectedCount: number;
}

/** DELETE /detections/:jobId payload. */
export interface CancelDetectionResult {
  id: string;
  status: 'cancelled';
  r2Deleted: number;
  r2Failed: number;
}

// ─── API Calls ───────────────────────────────────────────────────────

/**
 * POST /api/v2/sites/:siteId/detections
 * Create a detection job. The server enforces one in-flight scan per
 * (site, ortho layer) — a legitimate concurrent scan surfaces as a 409.
 */
export async function createDetection(
  siteId: string,
  body: CreateDetectionRequest
): Promise<CreateDetectionResult> {
  return apiFetch<CreateDetectionResult>(
    `/api/v2/sites/${siteId}/detections`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

/**
 * GET /api/v2/sites/:siteId/detections/:jobId
 * Poll job status. The server applies R2-marker completion + the lazy
 * watchdog on this call, so polling IS the completion mechanism.
 */
export async function getDetectionJob(
  siteId: string,
  jobId: string
): Promise<DetectionJobInfo> {
  return apiFetch<DetectionJobInfo>(
    `/api/v2/sites/${siteId}/detections/${jobId}`
  );
}

/**
 * GET /api/v2/sites/:siteId/detections/:jobId/result
 * Fetch the result JSON (only available once status === 'complete').
 * Generous timeout: whole-ortho scans can emit thousands of candidates.
 */
export async function getDetectionResult(
  siteId: string,
  jobId: string
): Promise<DetectionResult> {
  return apiFetch<DetectionResult>(
    `/api/v2/sites/${siteId}/detections/${jobId}/result`,
    {},
    120_000
  );
}

/**
 * POST /api/v2/sites/:siteId/detections/:jobId/labels
 * Persist review verdicts (v2 training fuel — retained across cancel).
 */
export async function postDetectionLabels(
  siteId: string,
  jobId: string,
  acceptedIds: string[],
  rejectedIds: string[]
): Promise<PostDetectionLabelsResult> {
  return apiFetch<PostDetectionLabelsResult>(
    `/api/v2/sites/${siteId}/detections/${jobId}/labels`,
    {
      method: 'POST',
      body: JSON.stringify({ acceptedIds, rejectedIds }),
    }
  );
}

/**
 * DELETE /api/v2/sites/:siteId/detections/:jobId
 * Cancel/dismiss the job: flips the row to 'cancelled' (kept for a later
 * sweep) and deletes the marker/result R2 objects. Labels are retained.
 */
export async function cancelDetection(
  siteId: string,
  jobId: string
): Promise<CancelDetectionResult> {
  return apiFetch<CancelDetectionResult>(
    `/api/v2/sites/${siteId}/detections/${jobId}`,
    { method: 'DELETE' }
  );
}
